import AjaxRequest from "./AjaxRequest";

class DitecOverlayController
{

    /**
     * DitecOverlayController constructor
     *
     * @param debug
     */
    constructor(debug=false)
    {
        this.debug = debug;
        this.triggers = document.querySelectorAll('[data-overlay], .button--popup, .product__turntable__link');
        this.overlays = document.querySelectorAll('.overlay');
        this.closers = document.querySelectorAll('[data-hide-overlay]');
        this.currentOverlayID = null;
        this.currentOverlay = null;
        this.currentTriggerClass = '-current-overlay-trigger';
        this.init();
    }

    /**
     * Initialise components
     */
    init()
    {
        this.initTriggers();

        // Initialise the closers which dismiss overlays:
        for (const closer of this.closers) {
            this.initCloser(closer);
        }

        // Initialise overlays:
        for (const overlay of this.overlays) {
            this.initOverlay(overlay);
        }

        // Clicking outside an overlay should close them:
        window.addEventListener('click', () => {
            this.hideOverlay(this.currentOverlay);
        });

        // Prevent mousewheel from scrolling the page when an overlay is active:
        window.addEventListener('mousewheel', (e) => {
            if (document.body.classList.contains('-scrolling-disabled')) {
                const overlayContent = this.currentOverlay.querySelector('.overlay__content');
                const overlayContentIsScrollable = overlayContent.scrollHeight > overlayContent.clientHeight;
                if (overlayContentIsScrollable && (e.target === this.currentOverlay || this.currentOverlay.contains(e.target))) {
                    // The user is scrolling the overlay's content -> do nothing
                }
                else {
                    // The mousewheel would scroll the <body> -> deny that
                    e.preventDefault();
                }
            }
        }, {passive: false});
    }

    /**
     * Initialise triggers which open up overlays
     */
    initTriggers()
    {
        for (const trigger of this.triggers) {

            const showEvent = trigger.getAttribute('data-overlay-show-event') || 'click';

            let overlayID = null;
            let overlay = null;

            // Triggers with data-overlay:
            if (trigger.getAttribute('data-overlay')) {
                overlayID = trigger.getAttribute('data-overlay');
                overlay = this.getOverlay(overlayID);
            }

            trigger.addEventListener(showEvent, (e) => {
                e.stopPropagation();

                // Some triggers will load specific types of overlays when requested:
                if (
                    trigger.classList.contains('button--popup') ||
                    trigger.classList.contains('product__turntable__link')
                ) {
                    // If the overlay is not yet present -> create it:
                    if (!trigger.getAttribute('data-overlay')) {
                        overlayID = Date.now();
                        overlay = this.createOverlayFromScratch(overlayID, `ajax-overlay${trigger.classList.contains('button--popup--white') ? ' overlay--white': ''}`);
                        overlay.classList.add('-loading');
                        let uri;

                        // Some will require an iframe, some will inject content loaded via ajax request
                        switch (trigger.classList.contains('button--popup')) {

                            // Load content via ajax
                            case true:
                                uri = trigger.href + '?contentOnly=1&no_cache=1';
                                this.debug && console.log('[DitecOverlayController]', 'initTriggers()', 'Request overlay content from uri:', uri);
                                const ajaxRequest = new AjaxRequest({
                                    url: uri,
                                    callback: (result) => {
                                        if (!result) return;
                                        overlay.querySelector('div').innerHTML = result;
                                        overlay.classList.remove('-loading');
                                        const overlayBackground = overlay.querySelector('.overlay__background');
                                        if (overlayBackground) {
                                            overlay.appendChild(overlayBackground); // The ajax request delivers the overlay background within the content -> move it outside
                                        }
                                    }
                                });
                                ajaxRequest.send();
                                break;

                            // Create an <iframe>
                            default:
                                const iframe = document.createElement('iframe');
                                uri = trigger.href;
                                this.debug && console.log('[DitecOverlayController]', 'initTriggers()', 'Create <iframe> with uri:', uri);
                                iframe.setAttribute('scrolling', false);
                                iframe.src = uri;
                                iframe.classList.add('overlay__iframe');
                                overlay.appendChild(iframe);
                                overlay.classList.remove('-loading');
                                break;
                        }

                        trigger.setAttribute('data-overlay', overlayID);
                    }
                }

                // If the overlay does not exist or is already open
                if (!this.getOverlay(overlayID) || this.isCurrentlyOpen(overlayID)) return true;

                // If the overlay exists (or has been created) -> show
                e.preventDefault();
                this.toggleOverlay(overlayID);

            });

            // If the overlay is triggered by a mouseenter event, we will also need a mouseleave event that hides the overlay
            // If the user hovers over the overlay's content within 1000ms this will be stopped however
            if ('mouseenter' === showEvent) {
                let mouseleaveTimeout = null;
                trigger.addEventListener('mouseleave', () => {
                    mouseleaveTimeout = setTimeout(() => {
                        this.hideOverlay(overlay);
                    }, 1500);
                    const overlayContent = overlay.querySelector('.overlay__content');
                    if (overlayContent) {
                        overlayContent.addEventListener('mouseenter', () => {
                            clearTimeout(mouseleaveTimeout);
                        });
                    }
                });
            }

            trigger.setAttribute('data-overlay-initialised', 1);
        }
    }

    /**
     * Initialise a close button
     *
     * @param {HTMLElement} closer
     */
    initCloser(closer)
    {
        closer.addEventListener('click', (e) =>
        {
            e.stopPropagation();
            const overlayID = closer.getAttribute('data-hide-overlay');
            if (!this.getOverlay(overlayID)) {
                return true;
            }
            this.hideOverlay(this.getOverlay(overlayID));
        });
    }

    /**
     * Initialise an overlay
     * Handles click events so that the overlay only closes on click outside
     * Moves the overlay to the end of <body>
     *
     * @param {HTMLElement} overlay
     */
    initOverlay(overlay)
    {
        overlay.addEventListener('click', (e) => {
            if (e.target.tagName !== 'BUTTON') {
                e.stopPropagation();
            }
            if (e.target === overlay) { // Click on transparent background ;-)
                this.hideOverlay(overlay);
            }
        });
        if ('mouseleave'=== overlay.getAttribute('data-overlay-hide-event')) {
            const overlayContent = overlay.querySelector('.overlay__content');
            if (overlayContent) {
                overlayContent.addEventListener('mouseleave', () => {
                    this.hideOverlay(overlay);
                });
            }
        }
        overlay.timeout = null;
        document.body.appendChild(overlay);
        this.debug && console.log('[DitecOverlayController]', 'initOverlay()', overlay);
    }

    /**
     * Refresh should be called after an AjaxRequest
     * Referenced in DitecProductSlider::initOverlays()
     */
    refresh()
    {
        this.triggers = document.querySelectorAll('[data-overlay]:not([data-overlay-initialised]), .button--popup:not([data-overlay-initialised])');
        this.initTriggers();
        this.debug && console.log('[DitecOverlayController]', 'refresh()');
    }

    /**
     * Get overlay
     * @param {string} overlayID
     * 
     * @returns {HTMLElement}
     */
    getOverlay(overlayID)
    {
        const overlay = document.getElementById(overlayID);
        this.debug && console.log('[DitecOverlayController]', `getOverlay(${overlayID})`, overlay);
        return overlay;
    }

    /**
     * Set current overlay
     *
     * @param {string} overlayID
     */
    setCurrentOverlay(overlayID)
    {
        this.currentOverlayID = overlayID;
        this.currentOverlay = document.getElementById(overlayID);
    }

    /**
     * Toggle overlay
     *
     * @param {string} overlayID
     */
    toggleOverlay(overlayID)
    {
        if (this.currentOverlayID === overlayID && this.currentOverlay.classList.contains('-visible')) {
            return;
        }
        this.setCurrentOverlay(overlayID);
        this.hideAllOtherOverlays();
        this.showCurrentOverlay();
        this.debug && console.log('[DitecOverlayController]', `toggleOverlay(${overlayID})`);

    }

    /**
     * Show the current overlay and activate its associated triggers
     */
    showCurrentOverlay()
    {
        this.currentOverlay.style.display = 'block';
        this.currentOverlay.style.zIndex = 100000;
        clearTimeout(this.currentOverlay.timeout);
        //this.currentOverlay.timeout = null;
        this.currentOverlay.timeout = setTimeout(() => {
            this.currentOverlay.classList.add('-visible');
        }, 0);
        document.body.classList.add('-scrolling-disabled');
        for (const currentTrigger of document.querySelectorAll(`[data-overlay="${this.currentOverlayID}"]`)) {
            currentTrigger.classList.add(this.currentTriggerClass);
        }
        this.debug && console.log('[DitecOverlayController]', 'showCurrentOverlay()', this.currentOverlay);
    }

    /**
     * Hide overlay
     *
     * @param {HTMLElement} overlay
     */
    hideOverlay(overlay)
    {
        if (null === overlay || !overlay.classList.contains('-visible')) {
            return;
        }
        overlay.classList.remove('-visible');
        overlay.timeout = setTimeout(() => {
            overlay.style.display = 'none';
            overlay.style.zIndex = -1;
        }, 1500);
        document.body.classList.remove('-scrolling-disabled');
        for (const currentTrigger of document.querySelectorAll('.-current-overlay-trigger')) {
            currentTrigger.classList.remove(this.currentTriggerClass);
        }
        this.debug && console.log('[DitecOverlayController]', `hideOverlay()`);
        overlay.dispatchEvent(new CustomEvent('hide'));
    }

    /**
     * Hide all other overlays
     */
    hideAllOtherOverlays()
    {
        for (const overlay of this.overlays) {
            if (overlay !== this.currentOverlay) {
                this.hideOverlay(overlay);
            }
        }
    }

    /**
     * Create overlay from scratch
     * Used, if a desired overlay is dynamically created and not initially present in HTML
     *
     * @param {string} id
     * @param {string} className
     * @returns {HTMLElement}
     */
    createOverlayFromScratch(id, className='')
    {
        const overlay = document.createElement('aside');
        if (className) overlay.setAttribute('class', className);
        overlay.classList.add('overlay');
        overlay.id = id;
        const closer = document.createElement('button');
        closer.setAttribute('data-hide-overlay', id);
        overlay.appendChild(closer);
        overlay.innerHTML += '<div class="overlay__content"></div>';
        this.debug && console.log('[DitecOverlayController]', 'createOverlayFromScratch()', overlay);
        this.initCloser(closer);
        this.initOverlay(overlay);
        return overlay;
    }

    /**
     * Check if overlay with specified is currently open
     *
     * @param {string} overlayID
     * @returns {boolean}
     */
    isCurrentlyOpen(overlayID)
    {
        return !this.currentOverlay ? false : this.currentOverlay.classList.contains('-visible') && overlayID === this.currentOverlayID;
    }

}

export default DitecOverlayController;